/* Small shared presentational pieces: progress bar, collapsible section,
   KPI card, page header, field legend, empty state, variant switch. */
import React, { type CSSProperties, type ReactNode } from 'react';
import { ic, sized } from '@/components/Icons';
import { useI18n } from '@/i18n';

/* ---------- ProgressBar ---------- */

export interface ProgressBarProps {
  pct: number;
  label?: ReactNode;
  sub?: ReactNode;
  color?: string;
}

export function ProgressBar({ pct, label, sub, color }: ProgressBarProps) {
  const c =
    color ||
    (pct === 100 ? 'var(--ok)' : pct >= 70 ? 'var(--brand-primary)' : pct >= 40 ? 'var(--warn)' : 'var(--err)');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label || sub ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-2)', fontWeight: 600 }}>
          <span>{label}</span>
          <span style={{ color: c }}>
            <span className="mono">{pct}%</span>{' '}
            {sub ? <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>· {sub}</span> : null}
          </span>
        </div>
      ) : null}
      <div style={{ background: 'var(--bg-2)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', background: c, height: '100%', borderRadius: 99, transition: 'width .3s ease' }} />
      </div>
    </div>
  );
}

/* ---------- Section (collapsible) ---------- */

export interface SectionProps {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  badge?: ReactNode;
  right?: ReactNode;
  tone?: string;
}

export function Section({ title, children, defaultOpen = true, badge, right, tone }: SectionProps) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        aria-expanded={open}
        style={{
          width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
          padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ display: 'inline-flex', color: 'var(--text-3)', transform: open ? 'rotate(0)' : 'rotate(-90deg)', transition: 'transform .2s' }}>
            {sized(ic.chevD, 16)}
          </span>
          {tone ? <span style={{ width: 8, height: 8, borderRadius: 99, background: tone }} /> : null}
          <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>{title}</span>
          {badge}
        </div>
        <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {right}
        </div>
      </div>
      {open ? <div style={{ padding: '4px 14px 14px', borderTop: '1px solid var(--line)' }}>{children}</div> : null}
    </div>
  );
}

/* ---------- KPI card ---------- */

export interface KPIProps {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  icon?: ReactNode;
  accent?: string;
  onClick?: () => void;
  goLabel?: string;
}

export function KPI({ label, value, sub, icon, accent = 'var(--brand-primary)', onClick, goLabel }: KPIProps) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={'card' + (clickable ? ' kpi-clickable' : '')}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      style={{
        padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 96,
        cursor: clickable ? 'pointer' : 'default', position: 'relative',
        transition: 'box-shadow .15s ease, transform .15s ease, border-color .15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>
          {label}
        </span>
        <span style={{ color: accent, display: 'inline-flex' }}>{icon}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {sub ? <div style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>{sub}</div> : null}
      {clickable ? (
        <span
          className="kpi-go"
          style={{
            position: 'absolute', right: 12, bottom: 12, display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 11, fontWeight: 700, color: accent, opacity: 0, transform: 'translateX(-4px)',
            transition: 'opacity .15s ease, transform .15s ease',
          }}
        >
          {goLabel ? <span>{goLabel}</span> : null}
          {sized(ic.chevR, 14)}
        </span>
      ) : null}
    </div>
  );
}

/* ---------- Field legend ---------- */

export function FieldLegend() {
  const { t } = useI18n();
  const items = [
    { key: 'editable', color: 'var(--st-edit)', icon: ic.edit, label: t('field.state_editable'), tip: t('field.state_tip_edit') },
    { key: 'readonly', color: 'var(--st-read)', icon: ic.eye,  label: t('field.state_readonly'), tip: t('field.state_tip_read') },
    { key: 'required', color: 'var(--st-req)',  icon: ic.warn, label: t('field.state_required'), tip: t('field.state_tip_req') },
    { key: 'blocked',  color: 'var(--st-lock)', icon: ic.lock, label: t('field.state_blocked'),  tip: t('field.state_tip_lock') },
  ];
  return (
    <div className="card" style={{ padding: '10px 14px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: 'var(--text-2)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginRight: 4 }}>
        {t('field.legend')}
      </span>
      {items.map((it) => (
        <div key={it.key} data-tip={it.tip} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 3, height: 14, background: it.color, borderRadius: 2, display: 'inline-block' }} />
          <span style={{ color: it.color, display: 'inline-flex' }}>{sized(it.icon, 13, 2)}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Page header ---------- */

export interface Crumb {
  label: ReactNode;
  onClick?: () => void;
}

export interface PageHeaderProps {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  crumbs?: Crumb[];
}

export function PageHeader({ title, sub, actions, crumbs }: PageHeaderProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        {crumbs?.length ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <span>›</span> : null}
                {c.onClick ? (
                  <button
                    className="btn ghost"
                    style={{ padding: 0, fontSize: 12, color: i === crumbs.length - 1 ? 'var(--text-2)' : 'var(--text-3)' }}
                    onClick={c.onClick}
                  >
                    {c.label}
                  </button>
                ) : (
                  <span style={{ color: i === crumbs.length - 1 ? 'var(--text-2)' : 'var(--text-3)' }}>{c.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        ) : null}
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-0.012em', color: 'var(--brand-ink)' }}>{title}</h1>
        {sub ? <p style={{ margin: '4px 0 0', color: 'var(--text-2)', fontSize: 13 }}>{sub}</p> : null}
      </div>
      {actions ? <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div> : null}
    </div>
  );
}

/* ---------- Empty state ---------- */

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ icon, title, body, action }: EmptyStateProps) {
  return (
    <div className="card" style={{ padding: 36, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
      <div style={{ color: 'var(--text-3)' }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
      {body ? <div style={{ color: 'var(--text-2)', fontSize: 13, maxWidth: 420 }}>{body}</div> : null}
      {action}
    </div>
  );
}

/* ---------- VariantSwitch (per-screen views) ---------- */

export interface VariantOption {
  value: string;
  es: string;
  en: string;
}

export interface VariantSwitchProps {
  value: string;
  onChange: (v: string) => void;
  options?: VariantOption[];
  exclude?: string[];
}

/** Falls back to A/B/C labelled "Variante X". Renders nothing when ≤1 option. */
export function VariantSwitch({ value, onChange, options, exclude }: VariantSwitchProps) {
  const { lang } = useI18n();
  let opts: VariantOption[] =
    options || ['A', 'B', 'C'].map((v) => ({ value: v, es: 'Variante ' + v, en: 'Variant ' + v }));
  if (exclude?.length) opts = opts.filter((o) => !exclude.includes(o.value));
  if (opts.length <= 1) return null;

  return (
    <div role="tablist" aria-label="variant" style={{ display: 'inline-flex', background: 'var(--bg-2)', borderRadius: 8, padding: 2, border: '1px solid var(--line)' }}>
      {opts.map((o) => {
        const active = value === o.value;
        const style: CSSProperties = {
          background: active ? 'var(--surface)' : 'transparent',
          border: 'none',
          boxShadow: active ? 'var(--shadow-1)' : 'none',
          color: active ? 'var(--brand-ink)' : 'var(--text-2)',
          fontWeight: 700, fontSize: 11, padding: '5px 11px', minWidth: 28, whiteSpace: 'nowrap',
        };
        return (
          <button key={o.value} className="btn sm" role="tab" aria-selected={active} onClick={() => onChange(o.value)} style={style}>
            {lang === 'es' ? o.es : o.en}
          </button>
        );
      })}
    </div>
  );
}
